// HUMINT subject page and Subjekty (PRD-0032 UC-4, slice 01, ADR-0044, ADR-0045).
//
// The page read and the list read are audited: every call records one `subject.view` row before
// it answers, so a caller fetches them once per load and never refetches them in the background.
// The capture-state poll answers ids and states only and is not audited. The shapes mirror `routers/v1/schemas/case_humint_subjects.py`. Imported by
// path, not via the barrel.
import type { CaseRole, ProcessingStageDto } from './cases'
import { apiClient } from './client'
import type { LegalStatusDto } from './humint'
import type { MarkRowDto } from './humint-marks'
import { humintPath } from './humint-path'
import type { SubjectVoiceLeadsDto } from './humint-voice-matching'

/** The subject's most recent encounter. */
export interface SubjectEncounterBriefDto {
  type: string
  at: string
}

/** A subject as every HUMINT row shows it; `display_name` null reads "Neznámy · 14:05 · 7f3a". */
export interface SubjectSummaryDto {
  /** The Person's graph elementId; URL-encode it in a path. */
  subject_ref: string
  /** First 4 hex of sha256(elementId). */
  short_id: string
  display_name: string | null
  nickname: string | null
  name_native: string | null
  /** `/api/v1/files/…` URL, loaded with the operator's token. */
  portrait_url: string | null
  latest_capture_tag: string | null
  legal_status: string | null
  last_encounter: SubjectEncounterBriefDto | null
  enrolled_at: string | null
  /**
   * Required steps of the person's record resolved, of all required ones (slice 03): what the
   * page's `checklist` counts. Null where not computed.
   */
  checklist?: ChecklistProgressDto | null
}

/**
 * Required steps done or skipped, of all required steps: of the person's record on a subject, of
 * one encounter's type on a row of the encounter log. A recommended step never counts.
 */
export interface ChecklistProgressDto {
  done: number
  total: number
}

/** Where an encounter happened; `name` is the place typed in, null when only the point is known. */
export interface EncounterPlaceDto {
  name: string | null
  lat: number | null
  lon: number | null
  accuracy_m: number | null
  source: 'device' | 'manual' | null
}

/** One encounter of the subject, with this subject's values on it. */
export interface SubjectEncounterDto {
  /** `enc-<uuid>`. */
  encounter_ref: string
  type: string
  occurred_at: string | null
  place: EncounterPlaceDto | null
  capturing_unit: string | null
  capture_tag: string | null
  legal_status: string | null
  check_verdict: string | null
  /** The other subjects of this encounter, summarised in `people`. */
  other_subject_refs: string[]
}

/**
 * What the server reports of a capture (slice 02): `processing` while its job runs; `done` once
 * a face of it is confirmed to the subject (a face view) or its job finished (any other kind);
 * `no_face` a face view with no confirmed face; `failed` its job failed; `skipped` a checklist
 * step skipped with a reason. A voice sample (ADR-0062) is `done` once its voiceprint is kept at
 * the subject, and `no_speech` when its job finished without one: too little speech in it.
 */
export type CaptureStateDto = 'processing' | 'done' | 'no_face' | 'no_speech' | 'failed' | 'skipped'

/**
 * What the server says of a stored voice sample's quality (ADR-0062), read from its voiceprint:
 * the grade (`good`, `ok`, `low`), the reasons that lowered it (`too_short`, `noisy`, `clipped`,
 * `quiet`, `narrowband`, `second_voice`), the net speech it measured, and whether the sample is
 * good enough to stand as a reference. Never a vector.
 */
export interface VoiceQualityDto {
  grade: string
  flags: string[]
  speech_seconds: number
  reference: boolean
  duration_seconds?: number | null
}

/** One capture; a skipped step has no document. */
export interface SubjectCaptureDto {
  capture_id: string
  kind: string
  captured_at: string
  document_id: string | null
  encounter_ref: string | null
  state: CaptureStateDto
  skip_reason?: string | null
  /** A photo added from a file, not taken in the workspace (ADR-0059). */
  imported?: boolean
  /** When an imported photo says it was taken; null when it says nothing. */
  taken_at?: string | null
  /**
   * A voice sample's quality: null until its voiceprint exists, and for every other kind. Absent
   * from a server that does not judge samples yet.
   */
  voice_quality?: VoiceQualityDto | null
}

/** One step of the person's record and the capture that answers it. */
export interface ChecklistStepDto {
  kind: string
  /**
   * True: the type of one of the subject's encounters requires the kind. False: a recommended
   * step, a kind some checklist names and none of the subject's encounters requires.
   */
  required: boolean
  /**
   * The latest capture of the kind that is done, else the latest one: of the subject, from any
   * encounter or from none (its `encounter_ref` may differ from the checklist's).
   */
  capture: SubjectCaptureDto | null
}

/**
 * The record of the person (slice 03), not of one encounter. Its required steps are the union of
 * what the types of ALL the subject's encounters require (the checkpoint steps without an
 * encounter), so from the first encounter on another one never lowers what it asks.
 */
export interface SubjectChecklistDto {
  /**
   * The subject's current encounter, the latest that has happened: captures and skips taken
   * from the record send it as `same_encounter_ref`. Null without an encounter. It does not
   * limit what answers the steps.
   */
  encounter_ref: string | null
  /** The type of that encounter. */
  encounter_type: string | null
  /**
   * The required steps first, then the recommended ones; each tier in the record order: the five
   * face views, the full body, the ID's front and back, a mark's close-up.
   */
  steps: ChecklistStepDto[]
  /** Required steps resolved; the recommended ones never count. */
  done: number
  /** All required steps. */
  total: number
}

/**
 * What the capture-state poll answers per capture: nothing about the subject. A voice sample
 * whose voiceprint is kept comes with its quality, in the same answer that says it is `done`.
 */
export interface CaptureStateRowDto {
  capture_id: string
  state: CaptureStateDto
  voice_quality?: VoiceQualityDto | null
}

/** One shot of a mark: a photo, or a skipped step with its reason (`url` null). */
export interface SubjectMarkPhotoDto {
  capture_id: string
  role: 'orientation' | 'closeup' | 'scale'
  captured_at: string
  /** `/api/v1/files/…`, loaded with the operator's token. */
  url: string | null
  /** The close-up's crop box `[x1, y1, x2, y2]` in 0..1. */
  box: [number, number, number, number] | null
  skip_reason: string | null
  /** A photo added from a file, not taken in the workspace (ADR-0059). */
  imported?: boolean
  /** When an imported photo says it was taken; null when it says nothing. */
  taken_at?: string | null
}

/** A mark of the subject (docs/slices/humint-marks-and-tattoos/), in pin-number order. */
export interface SubjectMarkDto extends MarkRowDto {
  /** The pin number: the subject's marks in creation order, from 1. */
  number: number
  /** `ok` or `low` once a tattoo's features are computed. */
  feature_quality: string | null
  /** The tattoo's crop, once its features are computed. */
  crop_url: string | null
  pending_candidates: number
  /** Oldest first. */
  photos: SubjectMarkPhotoDto[]
}

/** The state of a recording's job. */
export type RecordingStateDto = 'processing' | 'done' | 'failed'

/** The encounter a recording is held in: the header every encounter row shows. */
export interface RecordingEncounterDto {
  /** `enc-<uuid>`. */
  encounter_ref: string
  type: string
  occurred_at: string | null
  place: EncounterPlaceDto | null
  capturing_unit: string | null
}

/**
 * What every read says of a recording (ADR-0057): whose it is, when it was made, where its job
 * stands, the encounter it is held in and who else took part. A row of *Výsluchy*, the head of
 * the recording's page, and a recording the subject page lists beside its subject's own.
 */
export interface RecordingListItemDto {
  capture_id: string
  /** The subject who holds the recording. */
  subject: SubjectSummaryDto
  captured_at: string
  state: RecordingStateDto
  /** The stage the latest job is in; an older server sends none. */
  stage?: ProcessingStageDto | null
  /** The stage the job failed in, else null; a failed job names the same stage in both. */
  failed_stage?: ProcessingStageDto | null
  duration_ms: number | null
  /** Null for none, or for one that is no live encounter of the case. */
  encounter: RecordingEncounterDto | null
  /** The other subjects who took part, in the order they were linked. */
  participants: SubjectSummaryDto[]
}

/**
 * The open tattoo leads touching the subject: pairs of its marks with marks of another person of
 * the case that wait for a decision or for a merge.
 */
export interface SubjectTattooLeadsDto {
  count: number
  /** The subject's own mark in its first lead (the ones to merge first); null when `count` is 0. */
  mark_id: string | null
}

/** Original identity fields, separate from the display name used by lists. */
export interface SubjectEditableDto {
  name: string | null
  first_name: string | null
  last_name: string | null
  nickname: string | null
  rank: string | null
  date_of_birth: string | null
  service_number: string | null
}

export interface SubjectUpdateRequest extends Partial<SubjectEditableDto> {
  detention?: {
    encounter_ref: string
    capture_tag?: string | null
    legal_status?: LegalStatusDto | null
  }
}

export interface SubjectPageResponse {
  /** Absent on older servers, where editing is unavailable. */
  editable?: SubjectEditableDto
  /** The `subject.view` audit row's correlation id. */
  view_id: string
  subject: SubjectSummaryDto
  /** Newest first. */
  encounters: SubjectEncounterDto[]
  /** The subjects met together with this one. */
  people: SubjectSummaryDto[]
  /** The photos, newest first (a skipped step shows in the checklist only). */
  captures: SubjectCaptureDto[]
  /**
   * A small picture of each stored photo, by `capture_id`: a data URI
   * (`data:image/jpeg;base64,…`), part of this read and drawn as it is, never fetched. A photo
   * the server has no thumbnail of has no key, a recording and a skipped step never have one;
   * an older server sends no map. It covers `captures` and the checklist's steps alike.
   */
  capture_thumbnails?: Record<string, string> | null
  marks: SubjectMarkDto[]
  /** The recordings the subject took part in and does not hold, newest first; its own are captures. */
  joint_recordings?: RecordingListItemDto[]
  /** The record of the person: required steps, then recommended ones. */
  checklist?: SubjectChecklistDto | null
  /** The subject's open tattoo leads; an older server sends none. */
  tattoo_leads?: SubjectTattooLeadsDto | null
  /** The subject's open voice leads (zero and nulls without one); an older server sends none. */
  voice_leads?: SubjectVoiceLeadsDto | null
  /**
   * The caller's role as this read granted it; a superuser reads as `owner`. A `viewer` gets the
   * page read-only (the write routes answer 403 anyway).
   */
  member_role: CaseRole
}

export interface SubjectListItemDto {
  subject: SubjectSummaryDto
  /** When the subject last had workspace activity (enrolment, encounter, capture). */
  activity_at: string | null
  /** Open tattoo leads touching the subject; an older server sends none. */
  tattoo_leads?: number
  /** Open voice leads touching the subject; an older server sends none. */
  voice_leads?: number
}

export interface SubjectListResponse {
  /** Newest activity first. */
  items: SubjectListItemDto[]
  /** Every subject with workspace activity in the case. */
  total: number
  offset: number
  limit: number
}

/** Rows per *Subjekty* page (the API allows 1–100). */
export const SUBJECT_LIST_PAGE_SIZE = 50

export const humintSubjectPageApi = {
  update: (caseId: string, subjectRef: string, body: SubjectUpdateRequest) =>
    apiClient.patch<void>(humintPath(caseId, 'subjects', subjectRef), body),
  /** The subject page; the server records one `subject.view` row per call (a 503 records none). */
  page: (caseId: string, subjectRef: string, signal?: AbortSignal) =>
    apiClient.get<SubjectPageResponse>(humintPath(caseId, 'subjects', subjectRef), { signal }),

  /**
   * The state of every capture of the subject, for polling while any is processing. Ids and
   * states only, so it is not audited; the page read that listed them was.
   */
  captureStates: (caseId: string, subjectRef: string, signal?: AbortSignal) =>
    apiClient.get<CaptureStateRowDto[]>(
      humintPath(caseId, 'subjects', subjectRef, 'capture-states'),
      { signal }
    ),

  list: (caseId: string, offset: number, signal?: AbortSignal) =>
    apiClient.get<SubjectListResponse>(humintPath(caseId, 'subjects'), {
      params: { offset, limit: SUBJECT_LIST_PAGE_SIZE },
      signal,
    }),
}
